<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  items: { type: Array, required: true },
  modelValue: { type: Array, required: true },
  placeholder: { type: String, default: 'Selecionar' },
  emptyStateMessage: { type: String, default: 'Nenhum item encontrado.' },
  alignRight: { type: Boolean, default: false },
  showSelectAll: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const isOpen = ref(false);

const isSelected = id => props.modelValue.includes(id);

const toggleItem = id => {
  const next = isSelected(id)
    ? props.modelValue.filter(itemId => itemId !== id)
    : [...props.modelValue, id];
  emit('update:modelValue', next);
};

const selectAll = () =>
  emit(
    'update:modelValue',
    props.items.map(item => item.id)
  );
const deselectAll = () => emit('update:modelValue', []);

const buttonLabel = computed(() => {
  if (!props.modelValue.length) return props.placeholder;
  return t('REPORT_DASHBOARD.FILTER.SELECTED_COUNT', {
    count: props.modelValue.length,
  });
});
</script>

<template>
  <div
    v-on-click-outside="() => (isOpen = false)"
    class="relative inline-block"
  >
    <button
      type="button"
      class="px-3 py-1 text-xs uppercase tracking-wide border border-n-weak text-n-slate-11"
      @click="isOpen = !isOpen"
    >
      {{ buttonLabel }}
    </button>

    <div
      v-if="isOpen"
      class="absolute z-10 mt-1 w-64 max-h-64 overflow-y-auto no-scrollbar border border-n-strong bg-n-solid-1 p-2 shadow-lg"
      :class="alignRight ? 'right-0' : 'left-0'"
    >
      <div
        v-if="showSelectAll && items.length"
        class="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 pb-2 mb-1 border-b border-n-weak"
      >
        <button
          type="button"
          class="text-xs text-n-brand hover:underline whitespace-nowrap"
          @click="selectAll"
        >
          {{ t('REPORT_DASHBOARD.FILTER.SELECT_ALL') }}
        </button>
        <button
          type="button"
          class="text-xs text-n-brand hover:underline whitespace-nowrap"
          @click="deselectAll"
        >
          {{ t('REPORT_DASHBOARD.FILTER.DESELECT_ALL') }}
        </button>
      </div>
      <label
        v-for="item in items"
        :key="item.id"
        class="flex items-center gap-2 py-1 text-sm text-n-slate-12 cursor-pointer"
      >
        <Checkbox
          :model-value="isSelected(item.id)"
          @update:model-value="toggleItem(item.id)"
        />
        {{ item.name }}
      </label>
      <p v-if="!items.length" class="py-1 text-sm text-n-slate-11">
        {{ emptyStateMessage }}
      </p>
    </div>
  </div>
</template>
